import React, { useEffect, useMemo, useRef, useState } from "react";
import { DialogControl, FieldControl } from "spark-core-dx/components";
import { useGenericManager } from "spark-core-dx/hooks";
import { SystemTypes } from "spark-core-dx/services";
import { Operations } from "../../../services/ItemServices";
import { openMarketplaceCheckoutPanel } from "../marketplaceCheckout";

const CLIENT_PRODUCT_OFFER_ITEM = "ClientProductOffer";
const RUNTIME_CAPACITY_OFFER_KIND = 4;
const UNLIMITED_INCLUDED_USAGE_UNITS = 2147483647;
const EMPTY_ARRAY = [];

export const DEFAULT_RUNTIME_CAPACITY_OFFERS = [
  {
    key: "SmallProcessingCapacity",
    name: "Small Processing Capacity",
    sku: "CDX-RC-SPC",
    offerKind: RUNTIME_CAPACITY_OFFER_KIND,
    priceSummary: "1 active small background run at a time; default VM size Standard_A1_v2.",
    currencyCode: "USD",
    billingPeriod: 1,
    baseAmount: 100,
    unitLabel: "active run",
    sortOrder: 1,
    capacityGrants: [{ capacityGrantKey: "SmallProcessingCapacity", displayName: "Small Processing Capacity", poolType: 1, isUnlimited: false, maxConcurrentRuns: 1 }]
  },
  {
    key: "SmallProcessingCapacity3x",
    name: "Small Processing Capacity x3",
    sku: "CDX-RC-SPC3",
    offerKind: RUNTIME_CAPACITY_OFFER_KIND,
    priceSummary: "3 active small background runs at a time; default VM size Standard_A1_v2. Includes a 10% bulk discount.",
    currencyCode: "USD",
    billingPeriod: 1,
    baseAmount: 270,
    unitLabel: "active runs",
    sortOrder: 2,
    capacityGrants: [{ capacityGrantKey: "SmallProcessingCapacity3x", displayName: "Small Processing Capacity x3", poolType: 1, isUnlimited: false, maxConcurrentRuns: 3 }]
  },
  {
    key: "SmallProcessingCapacity5x",
    name: "Small Processing Capacity x5",
    sku: "CDX-RC-SPC5",
    offerKind: RUNTIME_CAPACITY_OFFER_KIND,
    priceSummary: "5 active small background runs at a time; default VM size Standard_A1_v2. Includes a 20% bulk discount.",
    currencyCode: "USD",
    billingPeriod: 1,
    baseAmount: 400,
    unitLabel: "active runs",
    sortOrder: 3,
    capacityGrants: [{ capacityGrantKey: "SmallProcessingCapacity5x", displayName: "Small Processing Capacity x5", poolType: 1, isUnlimited: false, maxConcurrentRuns: 5 }]
  },
  {
    key: "MediumProcessingCapacity",
    name: "Medium Processing Capacity",
    sku: "CDX-RC-MPC",
    offerKind: RUNTIME_CAPACITY_OFFER_KIND,
    priceSummary: "1 active medium background run at a time; default VM size Standard_A2_v2.",
    currencyCode: "USD",
    billingPeriod: 1,
    baseAmount: 250,
    unitLabel: "active run",
    sortOrder: 4,
    capacityGrants: [{ capacityGrantKey: "MediumProcessingCapacity", displayName: "Medium Processing Capacity", poolType: 2, isUnlimited: false, maxConcurrentRuns: 1 }]
  },
  {
    key: "MediumProcessingCapacity3x",
    name: "Medium Processing Capacity x3",
    sku: "CDX-RC-MPC3",
    offerKind: RUNTIME_CAPACITY_OFFER_KIND,
    priceSummary: "3 active medium background runs at a time; default VM size Standard_A2_v2. Includes a 10% bulk discount.",
    currencyCode: "USD",
    billingPeriod: 1,
    baseAmount: 675,
    unitLabel: "active runs",
    sortOrder: 5,
    capacityGrants: [{ capacityGrantKey: "MediumProcessingCapacity3x", displayName: "Medium Processing Capacity x3", poolType: 2, isUnlimited: false, maxConcurrentRuns: 3 }]
  },
  {
    key: "MediumProcessingCapacity5x",
    name: "Medium Processing Capacity x5",
    sku: "CDX-RC-MPC5",
    offerKind: RUNTIME_CAPACITY_OFFER_KIND,
    priceSummary: "5 active medium background runs at a time; default VM size Standard_A2_v2. Includes a 20% bulk discount.",
    currencyCode: "USD",
    billingPeriod: 1,
    baseAmount: 1000,
    unitLabel: "active runs",
    sortOrder: 6,
    capacityGrants: [{ capacityGrantKey: "MediumProcessingCapacity5x", displayName: "Medium Processing Capacity x5", poolType: 2, isUnlimited: false, maxConcurrentRuns: 5 }]
  },
  {
    key: "LargeProcessingCapacity",
    name: "Large Processing Capacity",
    sku: "CDX-RC-LPC",
    offerKind: RUNTIME_CAPACITY_OFFER_KIND,
    priceSummary: "1 active large background run at a time; default VM size Standard_A4_v2.",
    currencyCode: "USD",
    billingPeriod: 1,
    baseAmount: 500,
    unitLabel: "active run",
    sortOrder: 7,
    capacityGrants: [{ capacityGrantKey: "LargeProcessingCapacity", displayName: "Large Processing Capacity", poolType: 3, isUnlimited: false, maxConcurrentRuns: 1 }]
  },
  {
    key: "LargeProcessingCapacity3x",
    name: "Large Processing Capacity x3",
    sku: "CDX-RC-LPC3",
    offerKind: RUNTIME_CAPACITY_OFFER_KIND,
    priceSummary: "3 active large background runs at a time; default VM size Standard_A4_v2. Includes a 10% bulk discount.",
    currencyCode: "USD",
    billingPeriod: 1,
    baseAmount: 1350,
    unitLabel: "active runs",
    sortOrder: 8,
    capacityGrants: [{ capacityGrantKey: "LargeProcessingCapacity3x", displayName: "Large Processing Capacity x3", poolType: 3, isUnlimited: false, maxConcurrentRuns: 3 }]
  },
  {
    key: "LargeProcessingCapacity5x",
    name: "Large Processing Capacity x5",
    sku: "CDX-RC-LPC5",
    offerKind: RUNTIME_CAPACITY_OFFER_KIND,
    priceSummary: "5 active large background runs at a time; default VM size Standard_A4_v2. Includes a 20% bulk discount.",
    currencyCode: "USD",
    billingPeriod: 1,
    baseAmount: 2000,
    unitLabel: "active runs",
    sortOrder: 9,
    capacityGrants: [{ capacityGrantKey: "LargeProcessingCapacity5x", displayName: "Large Processing Capacity x5", poolType: 3, isUnlimited: false, maxConcurrentRuns: 5 }]
  },
  {
    key: "UnlimitedProcessingCapacity",
    name: "Unlimited Processing Capacity",
    sku: "CDX-RC-UPC",
    offerKind: RUNTIME_CAPACITY_OFFER_KIND,
    priceSummary: "Unlimited processing capacity; commercial terms are managed at the Product Offer level.",
    includedUnits: UNLIMITED_INCLUDED_USAGE_UNITS,
    unitLabel: "runtime capacity",
    sortOrder: 10,
    capacityGrants: [{ capacityGrantKey: "UnlimitedProcessingCapacity", displayName: "Unlimited Processing Capacity", poolType: null, isUnlimited: true, maxConcurrentRuns: 1 }]
  }
];

const readValue = (value, ...keys) => {
  if (value === null || value === undefined) return "";
  if (typeof value !== "object") return value;
  for (const key of keys) {
    if (value[key] !== undefined && value[key] !== null && value[key] !== "") {
      return value[key];
    }
  }
  return "";
};

const getFirst = (items) => {
  if (!items) return null;
  if (typeof items.first === "function") return items.first();
  return Array.isArray(items) ? items[0] : items;
};

const getFailureMessage = (result, fallback) => {
  const messages = result?.Messages || result?.messages || [];
  const message = getFirst(messages) || result?.Message || result?.message;
  return message?.DisplayMessage || message?.displayMessage || message?.Message || message?.message || fallback;
};

const getSelectedValue = (value) => {
  if (Array.isArray(value)) {
    return getSelectedValue(value[0]);
  }

  return readValue(value, "Value", "value", "Key", "key", "Id", "id")
    || value?.target?.value
    || value;
};

const hasOfferKey = (offers, offerKey) =>
  Boolean(offerKey) && offers.some((offer) =>
    String(offer.key || "").toLowerCase() === String(offerKey || "").toLowerCase()
  );

const normalizeOffer = (offer) => ({
  ...offer,
  key: readValue(offer, "key", "ProductOfferKey", "productOfferKey"),
  name: readValue(offer, "name", "DisplayName", "displayName") || readValue(offer, "key", "ProductOfferKey", "productOfferKey"),
  sku: readValue(offer, "sku", "Sku"),
  offerKind: Number(readValue(offer, "offerKind", "OfferKind") || 0),
  priceSummary: readValue(offer, "priceSummary", "PriceSummary"),
  currencyCode: readValue(offer, "currencyCode", "CurrencyCode") || "USD",
  billingPeriod: Number(readValue(offer, "billingPeriod", "BillingPeriod") || 1),
  baseAmount: Number(readValue(offer, "baseAmount", "BaseAmount") || 0),
  unitRate: Number(readValue(offer, "unitRate", "UnitRate") || 0),
  unitLabel: readValue(offer, "unitLabel", "UnitLabel"),
  reviewRequired: readValue(offer, "reviewRequired", "ReviewRequired") === true,
  customPricing: readValue(offer, "customPricing", "CustomPricing") === true,
  capacityGrants: offer?.capacityGrants || offer?.CapacityGrants || []
});

const normalizeOffers = (offers) => {
  const sourceOffers = Array.isArray(offers) && offers.length > 0 ? offers : DEFAULT_RUNTIME_CAPACITY_OFFERS;
  return sourceOffers
    .map(normalizeOffer)
    .filter((offer) => offer.offerKind === RUNTIME_CAPACITY_OFFER_KIND && offer.key)
    .sort((left, right) =>
      (Number(left.sortOrder || left.SortOrder || 0) - Number(right.sortOrder || right.SortOrder || 0))
      || String(left.name).localeCompare(String(right.name))
    );
};

const formatCurrency = (amount, currencyCode = "USD") => {
  const normalizedAmount = Number(amount || 0);
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currencyCode || "USD",
    maximumFractionDigits: normalizedAmount % 1 === 0 ? 0 : 2
  }).format(normalizedAmount);
};

const getBillingPeriodLabel = (billingPeriod) => {
  switch (Number(billingPeriod || 0)) {
    case 1:
      return "month";
    case 2:
      return "year";
    case 3:
      return "one time";
    case 4:
      return "manual";
    default:
      return "period";
  }
};

const buildPricingDisplay = (offer) => {
  if (offer?.reviewRequired) return "Review required";
  if (offer?.customPricing) return "Custom pricing";
  if (Number(offer?.baseAmount || 0) > 0) {
    return `${formatCurrency(offer.baseAmount, offer.currencyCode)} / ${getBillingPeriodLabel(offer.billingPeriod)}`;
  }
  if (Number(offer?.unitRate || 0) > 0) {
    return `${formatCurrency(offer.unitRate, offer.currencyCode)} per ${offer.unitLabel || "unit"}`;
  }
  return offer?.priceSummary || "Pricing managed on offer";
};

const getGrantQuantity = (offer) => {
  const grant = getCapacityGrant(offer);
  if (!grant || readValue(grant, "isUnlimited", "IsUnlimited") === true) return 1;
  return Math.max(1, Number(readValue(grant, "maxConcurrentRuns", "MaxConcurrentRuns") || 1));
};

const buildPurchaseTotalDisplay = (offer) => {
  if (offer?.reviewRequired) return "Review required";
  if (offer?.customPricing) return "Custom pricing";
  if (Number(offer?.baseAmount || 0) > 0) {
    return `${formatCurrency(offer.baseAmount, offer.currencyCode)} / ${getBillingPeriodLabel(offer.billingPeriod)}`;
  }
  if (Number(offer?.unitRate || 0) > 0) {
    return `${formatCurrency(offer.unitRate, offer.currencyCode)} per ${offer.unitLabel || "unit"}`;
  }
  return "Pricing managed on offer";
};

const buildCostPerGrantDisplay = (offer) => {
  if (offer?.reviewRequired) return "Review required";
  if (offer?.customPricing) return "Custom pricing";
  const grantQuantity = getGrantQuantity(offer);
  if (Number(offer?.baseAmount || 0) > 0) {
    return `${formatCurrency(Number(offer.baseAmount || 0) / grantQuantity, offer.currencyCode)} / grant / ${getBillingPeriodLabel(offer.billingPeriod)}`;
  }
  if (Number(offer?.unitRate || 0) > 0) {
    return `${formatCurrency(offer.unitRate, offer.currencyCode)} per ${offer.unitLabel || "grant"}`;
  }
  return "Pricing managed on offer";
};

const getCapacityGrant = (offer) => {
  const grants = Array.isArray(offer?.capacityGrants) ? offer.capacityGrants : [];
  return grants.find((grant) => readValue(grant, "isActive", "IsActive") !== false) || grants[0] || null;
};

const getPoolTypeLabel = (poolType) => {
  switch (Number(poolType || 0)) {
    case 1:
      return "Small pool";
    case 2:
      return "Medium pool";
    case 3:
      return "Large pool";
    default:
      return "Any active pool";
  }
};

const buildGrantDisplay = (offer) => {
  const grant = getCapacityGrant(offer);
  if (!grant) return "Runtime capacity grant";
  if (readValue(grant, "isUnlimited", "IsUnlimited") === true) {
    return "Unlimited runtime capacity";
  }
  const maxConcurrentRuns = Math.max(1, Number(readValue(grant, "maxConcurrentRuns", "MaxConcurrentRuns") || 1));
  return `${getPoolTypeLabel(readValue(grant, "poolType", "PoolType"))}; ${maxConcurrentRuns} active ${maxConcurrentRuns === 1 ? "run" : "runs"} per grant`;
};

const getOwnedQuantity = (clientProductOffers, offerKey) =>
  (clientProductOffers || [])
    .filter((clientOffer) => String(clientOffer.productOfferKey || clientOffer.ProductOfferKey || "").toLowerCase() === String(offerKey || "").toLowerCase())
    .reduce((total, clientOffer) => total + Number(clientOffer.quantity || clientOffer.Quantity || 1), 0);

const isInactiveClientProductOffer = (clientOffer) => {
  const lifecycleStatus = Number(readValue(clientOffer, "lifecycleStatus", "LifecycleStatus") || 0);
  const approvalStatus = Number(readValue(clientOffer, "approvalStatus", "ApprovalStatus") || 0);
  return [8, 9].includes(lifecycleStatus) || (approvalStatus > 0 && approvalStatus !== 3);
};

export const hasRuntimeCapacityClientOffer = (clientProductOffers = [], offerKey = "") =>
  (clientProductOffers || []).some((clientOffer) => {
    if (isInactiveClientProductOffer(clientOffer)) {
      return false;
    }

    const productOfferKey = readValue(clientOffer, "productOfferKey", "ProductOfferKey");
    const offerKind = Number(readValue(clientOffer, "offerKind", "OfferKind") || 0);
    const grants = clientOffer?.capacityGrants || clientOffer?.CapacityGrantsSnapshot || clientOffer?.capacityGrantsSnapshot || [];
    const hasActiveGrant = grants.some((grant) => readValue(grant, "isActive", "IsActive") !== false);
    const matchesOffer = !offerKey
      || String(productOfferKey || "").toLowerCase() === String(offerKey || "").toLowerCase()
      || grants.some((grant) => String(readValue(grant, "capacityGrantKey", "CapacityGrantKey") || "").toLowerCase() === String(offerKey || "").toLowerCase());

    return matchesOffer && (offerKind === RUNTIME_CAPACITY_OFFER_KIND || hasActiveGrant);
  });

export const getRuntimeCapacityActionVerb = ({
  clientProductOffers = [],
  offerKey = "",
  hasSelectedGrant = false,
  publicMode = false,
  fallbackActionVerb = "Buy"
} = {}) => {
  if (publicMode) return "Learn more";
  return hasSelectedGrant || hasRuntimeCapacityClientOffer(clientProductOffers, offerKey) ? "Add" : fallbackActionVerb;
};

const runtimeCapacityOfferField = {
  FieldName: "RuntimeCapacityProductOfferKey",
  DisplayName: "Runtime Capacity Plan",
  Description: "Select the runtime capacity grant to add for scheduled and pool-backed execution.",
  FieldType: SystemTypes.UIFieldType.SearchSelectList,
  Format: SystemTypes.UIFormats?.STRING,
  IsRequired: true
};

const MarketplaceRuntimeCapacityPurchaseDialog = ({
  open = false,
  product = null,
  offers = EMPTY_ARRAY,
  environmentKey = "",
  clientProductOffers = EMPTY_ARRAY,
  initialOfferKey = "",
  actionVerb = "Buy",
  isSelfHostedResourceMode = false,
  beforeCheckout,
  onClose,
  onPurchased
}) => {
  const clientProductOfferMgr = useGenericManager(CLIENT_PRODUCT_OFFER_ITEM);
  const selfHostedMode = isSelfHostedResourceMode;
  const normalizedOffers = useMemo(() => normalizeOffers(offers)
    .filter((offer) => !selfHostedMode || offer.key === "UnlimitedProcessingCapacity"),
  [offers, selfHostedMode]);
  const [selectedOfferKey, setSelectedOfferKey] = useState(initialOfferKey || "");
  const [isSaving, setIsSaving] = useState(false);
  const [actionError, setActionError] = useState("");
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      return;
    }

    setIsSaving(false);
    setActionError("");
    setSelectedOfferKey((currentOfferKey) => {
      if (wasOpenRef.current && hasOfferKey(normalizedOffers, currentOfferKey)) {
        return currentOfferKey;
      }

      if (hasOfferKey(normalizedOffers, initialOfferKey)) {
        return initialOfferKey;
      }

      return normalizedOffers[0]?.key || "";
    });
    wasOpenRef.current = true;
  }, [initialOfferKey, normalizedOffers, open]);

  const selectedOffer = normalizedOffers.find((offer) =>
    String(offer.key).toLowerCase() === String(selectedOfferKey || "").toLowerCase()
  ) || null;
  const selectedGrant = getCapacityGrant(selectedOffer);
  const selectedOwnedQuantity = selectedOffer ? getOwnedQuantity(clientProductOffers, selectedOffer.key) : 0;
  const selectedIsOwnedUnlimited = selectedOwnedQuantity > 0
    && selectedGrant
    && readValue(selectedGrant, "isUnlimited", "IsUnlimited") === true;
  const selectData = normalizedOffers.map((offer) => ({
    Text: `${offer.name} - ${buildGrantDisplay(offer)}`,
    DisplayName: `${offer.name} - ${buildGrantDisplay(offer)}`,
    Value: offer.key,
    Sequence: Number(offer.sortOrder || offer.SortOrder || 0),
    FilterValue: `${offer.name}|${offer.sku}|${buildGrantDisplay(offer)}`
  }));

  if (!open) {
    return null;
  }

  const handleClose = () => {
    if (!isSaving) {
      onClose?.();
    }
  };

  const handleConfirm = async () => {
    if (!selectedOffer || (selectedIsOwnedUnlimited && !selfHostedMode) || isSaving) {
      return undefined;
    }

    setIsSaving(true);

    if (!selfHostedMode) {
      try {
        const saveResult = await beforeCheckout?.();
        if (saveResult?.Success === false) {
          throw new Error("Save the current editor successfully before continuing to checkout.");
        }
        openMarketplaceCheckoutPanel({ offerKey: selectedOffer.key });
        onClose?.();
      } catch (ex) {
        setActionError(ex?.message || "The current editor could not be saved before checkout.");
      } finally {
        setIsSaving(false);
      }
      return undefined;
    }

    try {
      const result = await clientProductOfferMgr.RunOperation({
        operationName: Operations.ClientProductOfferItem?.AddRuntimeCapacityGrant || "AddRuntimeCapacityGrant",
        item: {
          IsNew: true,
          EnvironmentKey: environmentKey || "",
          ProductOfferKey: selectedOffer.key,
          Sku: selectedOffer.sku || ""
        },
        isItem: true,
        isWarning: true,
        supressAllErrors: false
      });

      if (result?.Success) {
        onPurchased?.(getFirst(result.Items) || result.Item || result.item || null, result, selectedOffer);
        onClose?.();
      }

      if (!result?.Success) {
        setActionError(getFailureMessage(result, "Runtime capacity grant could not be selected."));
      }

      return result;
    } catch (ex) {
      setActionError(ex?.message || "Runtime capacity grant could not be selected.");
      return {
        Success: false,
        Messages: [{ DisplayMessage: ex?.message || "Runtime capacity grant could not be added." }]
      };
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
    <DialogControl
      openDialog={open}
      title={product?.name ? `${selfHostedMode ? "Select" : actionVerb} grant - ${product.name}` : `${selfHostedMode ? "Select" : actionVerb} runtime capacity grant`}
      subTitle="Choose the capacity plan to add. The purchased grant becomes available to schedules and other pool-backed runtime flows."
      okText={selfHostedMode ? "Select" : actionVerb}
      cancelText="Cancel"
      onCancel={handleClose}
      onX={handleClose}
      onOk={handleConfirm}
      disableOk={!selectedOffer || (selectedIsOwnedUnlimited && !selfHostedMode) || isSaving}
      loadingOk={isSaving}
      className="marketplace-dialog-control marketplace-runtime-capacity-dialog-control"
      innerclassName="marketplace-dialog-control__inner"
      controlOrigin="marketplace-runtime-capacity-purchase"
    >
      <div className="marketplace-runtime-capacity-dialog">
        <FieldControl
          className="marketplace-runtime-capacity-dialog__field"
          fieldMetadata={runtimeCapacityOfferField}
          selectData={selectData}
          value={selectedOfferKey}
          groupId="runtime-capacity-product-offer-key"
          onChange={(value) => setSelectedOfferKey(String(getSelectedValue(value) || ""))}
          disableError
        />
        {selectedOffer ? (
          <div className="marketplace-runtime-capacity-dialog__summary">
            <span className="marketplace-eyebrow">{selectedOffer.sku || selectedOffer.key}</span>
            <strong>{selectedOffer.name}</strong>
            <p>{selectedOffer.priceSummary || buildPricingDisplay(selectedOffer)}</p>
            <dl className="marketplace-runtime-capacity-dialog__pricing">
              <div>
                <dt>Cost per grant</dt>
                <dd>{buildCostPerGrantDisplay(selectedOffer)}</dd>
              </div>
              <div>
                <dt>Purchase total</dt>
                <dd>{buildPurchaseTotalDisplay(selectedOffer)}</dd>
              </div>
            </dl>
            <div className="marketplace-badge-row">
              <span className="marketplace-badge marketplace-badge--neutral">{buildGrantDisplay(selectedOffer)}</span>
              {selectedGrant && readValue(selectedGrant, "isUnlimited", "IsUnlimited") === true ? (
                <span className="marketplace-badge marketplace-badge--review">Unlimited</span>
              ) : null}
              {selectedIsOwnedUnlimited ? <span className="marketplace-badge marketplace-badge--owned">Purchased</span> : null}
              <span className="marketplace-badge marketplace-badge--neutral">Owned: {selectedOwnedQuantity}</span>
            </div>
          </div>
        ) : (
          <div className="marketplace-dialog-control__empty">No runtime capacity plans are available.</div>
        )}
      </div>
    </DialogControl>
    <DialogControl
      controlOrigin="marketplace-runtime-capacity-purchase-error"
      title="Runtime Capacity Selection Error"
      openDialog={Boolean(actionError)}
      okText="Close"
      hideCancel
      onOk={() => setActionError("")}
      onCancel={() => setActionError("")}
      onX={() => setActionError("")}
    >
      <p>{actionError}</p>
    </DialogControl>
    </>
  );
};

export default MarketplaceRuntimeCapacityPurchaseDialog;
